import { App, Empty, Spin } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/it";
import type { CustomSeriesRenderItem, EChartsOption, EChartsType } from "echarts";
import { CustomChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomCard,
  CustomSelect,
  CustomSharedDatePicker,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import {
  useDriverActivities,
  useDriverActivitiesLookup,
} from "src/queries/driverActivities.queries";
import { useQueryGetClientLookup } from "src/queries/lookup.queries";
import type { LookupItem } from "src/services/lookup/lookupService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import {
  DRIVER_STATE_CONFIGS,
  getTotalDurationByState,
  mapActivityToStateKey,
  transformActivityData,
  type DriverStateKey,
} from "./driverActivities";

const { CustomText, CustomTitle } = CustomTypography;

echarts.use([TooltipComponent, GridComponent, CustomChart, CanvasRenderer]);

const getOptionalId = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
};

const getLookupItems = (payload: unknown): LookupItem[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const lookupPayload = payload as {
    response?: unknown;
    data?: unknown;
    items?: unknown;
  };
  if (Array.isArray(lookupPayload.response)) return lookupPayload.response;
  if (Array.isArray(lookupPayload.data)) return lookupPayload.data;
  if (Array.isArray(lookupPayload.items)) return lookupPayload.items;
  return [];
};

const formatMinutes = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const resolveCssVariable = (value: string) => {
  if (!value.startsWith("var(") || typeof window === "undefined") return value;
  const variableName = value.slice(4, -1).trim();
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || value;
};

interface ChartActivityItem {
  value: [number, number, number];
  stateKey: DriverStateKey;
  dddStatus: boolean;
  name: string;
}

const DriverActivitiesPage = () => {
  const { t, i18n } = useTranslation();
  const { selectedDistributor } = useSelectDistributor();
  const distributorId = getOptionalId(selectedDistributor);
  const language = i18n.language?.split("-")[0] || "en";
  const [customerId, setCustomerId] = useState<number | string | null>(null);
  const [driverId, setDriverId] = useState<number | string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(6, "days"),
    dayjs(),
  ]);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<EChartsType | null>(null);

  const { message } = App.useApp();

  useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  const customerLookupParams = useMemo(
    () => ({
      ...(distributorId ? { distributorId } : {}),
    }),
    [distributorId]
  );
  const customerLookup = useQueryGetClientLookup(customerLookupParams, {
    enabled: true,
  });
  const customerOptions = useMemo(() => {
    const payload = (customerLookup.data as { data?: unknown } | undefined)?.data;
    return getLookupItems(payload)
      .map((customer) => ({
        label: String(
          customer.companyName ?? customer.name ?? customer.label ?? customer.title ?? "--"
        ),
        value: customer.id ?? customer.value,
      }))
      .filter(
        (option): option is { label: string; value: string | number } =>
          option.value !== undefined && option.value !== null && option.value !== ""
      );
  }, [customerLookup.data]);

  useEffect(() => {
    setCustomerId(null);
    setDriverId(null);
  }, [distributorId]);

  useEffect(() => {
    if (!customerOptions.length) {
      setCustomerId(null);
      return;
    }
    if (!customerOptions.some((customer) => String(customer.value) === String(customerId))) {
      setCustomerId(customerOptions[0]?.value ?? null);
    }
  }, [customerId, customerOptions]);

  const driverLookupParams = useMemo(
    () => ({
      customerIds: customerId,
      onlyWithTachographId: true,
    }),
    [customerId]
  );
  const driverLookup = useDriverActivitiesLookup(driverLookupParams, Boolean(customerId));
  const drivers = useMemo(() => driverLookup.data?.data?.response ?? [], [driverLookup.data]);

  useEffect(() => {
    if (!drivers.length) {
      setDriverId(null);
      return;
    }
    if (!drivers.some((driver) => String(driver.id) === String(driverId))) {
      setDriverId(drivers[0]?.id ?? null);
    }
  }, [driverId, drivers]);

  const activityParams = useMemo(
    () => ({
      driverId,
      from: `${dateRange[0].format("YYYY-MM-DD")}T00:00:00`,
      to: `${dateRange[1].format("YYYY-MM-DD")}T23:59:59`,
      ...(customerId ? { customerId } : {}),
    }),
    [customerId, dateRange, driverId]
  );
  const activityQuery = useDriverActivities(activityParams, Boolean(customerId && driverId));
  const activityResponse = activityQuery.data?.data?.response;
  const timelineDays = useMemo(
    () => transformActivityData(activityResponse, language),
    [activityResponse, language]
  );
  const statusCode = activityQuery.data?.status;

  useEffect(() => {
    if (statusCode === 204 || !chartContainerRef.current) return undefined;

    const chart = echarts.init(chartContainerRef.current);
    chartInstanceRef.current = chart;
    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
      chartInstanceRef.current = null;
    };
  }, [statusCode]);

  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;
    if (!timelineDays.length) {
      chart.clear();
      return;
    }

    const dayLabels = timelineDays.map((day) => day.dateObj.locale(language).format("ddd, MMM D"));
    const seriesData: ChartActivityItem[] = timelineDays.flatMap((day, dayIndex) =>
      day.states
        .filter((activity) => activity.end > activity.start)
        .map((activity) => ({
          value: [dayIndex, activity.start, activity.end],
          stateKey: activity.state,
          dddStatus: activity.dddStatus,
          name: t(DRIVER_STATE_CONFIGS[activity.state].nameKey),
        }))
    );
    const dddLineColor = resolveCssVariable("var(--color-red)") || "#f5222d";

    const renderItem: CustomSeriesRenderItem = (params, api) => {
      const categoryIndex = Number(api.value(0));
      const start = Number(api.value(1)) / 60;
      const end = Number(api.value(2)) / 60;
      const dataItem = seriesData[params.dataIndexInside] ?? seriesData[params.dataIndex];
      if (!dataItem) return null;

      const config = DRIVER_STATE_CONFIGS[dataItem.stateKey];
      const coordStart = api.coord([start, categoryIndex]);
      const coordEnd = api.coord([end, categoryIndex]);
      const size = api.size?.([0, 1]);
      const categoryHeight = Array.isArray(size) ? Number(size[1]) : Number(size ?? 0);
      const barHeight = categoryHeight * 0.75;
      const coordSys = params.coordSys as typeof params.coordSys & {
        x: number;
        y: number;
        width: number;
        height: number;
      };
      const rectShape = echarts.graphic.clipRectByRect(
        {
          x: coordStart[0] ?? 0,
          y: (coordStart[1] ?? 0) - barHeight / 2,
          width: (coordEnd[0] ?? 0) - (coordStart[0] ?? 0),
          height: barHeight,
        },
        {
          x: coordSys.x,
          y: coordSys.y,
          width: coordSys.width,
          height: coordSys.height,
        }
      );
      if (!rectShape) return null;

      const resolvedColor = resolveCssVariable(config.color);
      const rectElement = {
        type: "rect",
        shape: rectShape,
        style: {
          fill: resolvedColor,
          decal: config.striped
            ? {
                symbol: "rect",
                symbolSize: 4,
                dashArrayX: [1, 2],
                dashArrayY: [2, 5],
                rotation: Math.PI / 4,
                color: resolveCssVariable("var(--color-green)"),
                backgroundColor: resolvedColor,
              }
            : undefined,
        },
        silent: false,
      };

      if (!dataItem.dddStatus) {
        return rectElement as ReturnType<CustomSeriesRenderItem>;
      }

      const baselineHeight = 2;
      const baselineWidth = Math.max((coordEnd[0] ?? 0) - (coordStart[0] ?? 0), baselineHeight);
      const baselineCandidate = (coordStart[1] ?? 0) + barHeight / 2 + 6;
      const baselineY = Math.min(
        baselineCandidate,
        coordSys.y + coordSys.height - baselineHeight - 1
      );

      return {
        type: "group",
        children: [
          rectElement,
          {
            type: "rect",
            shape: {
              x: coordStart[0] ?? 0,
              y: baselineY,
              width: baselineWidth,
              height: baselineHeight,
            },
            style: { fill: dddLineColor },
            silent: true,
          },
        ],
        silent: false,
      } as ReturnType<CustomSeriesRenderItem>;
    };

    const option = {
      grid: {
        left: 0,
        right: 0,
        top: 25,
        bottom: 0,
        containLabel: true,
      },
      tooltip: {
        trigger: "item",
        formatter: (rawParams: unknown) => {
          const params = rawParams as {
            value?: [number, number, number];
            data?: ChartActivityItem;
          };
          const startMinutes = Number(params.value?.[1] ?? 0);
          const endMinutes = Number(params.value?.[2] ?? 0);
          const stateKey = params.data?.stateKey ?? "NOT_AVAILABLE";
          return `<div class="custom-tooltip"><strong>${t(
            DRIVER_STATE_CONFIGS[stateKey].nameKey
          )}</strong><br/>${formatMinutes(startMinutes)} - ${formatMinutes(endMinutes)}</div>`;
        },
        extraCssText: "white-space: nowrap;",
      },
      xAxis: {
        type: "value",
        min: 0,
        max: 24,
        splitNumber: 24,
        axisLabel: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: dayLabels,
        axisTick: { alignWithLabel: true },
        inverse: true,
      },
      series: [
        {
          type: "custom",
          renderItem,
          itemStyle: { borderRadius: 4 },
          encode: { x: [1, 2], y: 0 },
          data: seriesData,
        },
      ],
    } as EChartsOption;

    chart.setOption(option, true);
    chart.resize();
  }, [language, t, timelineDays]);

  const summaryStates = useMemo(() => {
    const values = (activityResponse?.days ?? []).flatMap((day) =>
      (day.activities ?? []).map(mapActivityToStateKey)
    );
    return Array.from(new Set(values));
  }, [activityResponse]);

  const handleWeekEndingChange = (value: Dayjs | null) => {
    if (!value?.isValid()) return;
    const end = value.startOf("day").isAfter(dayjs().startOf("day"))
      ? dayjs().startOf("day")
      : value.startOf("day");
    setDateRange([end.subtract(6, "days"), end]);
  };

  const handleReset = () => {
    const end = dayjs().startOf("day");
    setDateRange([end.subtract(6, "days"), end]);
    message.success(t("reset_to_last_seven_day"));
  };

  const stateStyle = (state: DriverStateKey) => {
    const config = DRIVER_STATE_CONFIGS[state];
    return {
      backgroundColor: config.color,
      backgroundImage: config.striped
        ? "repeating-linear-gradient(45deg, var(--color-green) 7px, var(--color-green) 10px, transparent 6px, transparent 12px)"
        : undefined,
      backgroundSize: config.striped ? "24px" : undefined,
    };
  };
  const chartHeight = 400;
  const barHeightPx = (chartHeight * 0.8) / (timelineDays.length || 7);

  return (
    <section className="min-h-screen bg-gray1 pb-8">
      <CustomSharedTitleSection
        titleKey="driver_activities"
        level={1}
        showBtn={false}
        titleClassName="!text-2xl !font-bold"
      />

      <CustomCard>
        <div className="flex items-end justify-between mb-6">
          <div className="flex flex-wrap gap-3 flex-1 min-w-0">
            <CustomSelect
              label={`${t("select_customer")}:`}
              placeholder={t("select_customer")}
              value={customerId ?? undefined}
              onChange={(value: number | string) => {
                setCustomerId(value);
                setDriverId(null);
              }}
              loading={customerLookup.isLoading || customerLookup.isFetching}
              showSearch
              optionFilterProp="label"
              options={customerOptions}
              className="common-select w-[216px]"
              style={{ minWidth: 260 }}
            />
            <CustomSelect
              label={t("select_driver")}
              placeholder={t("select_driver")}
              value={driverId ?? undefined}
              onChange={(value: number | string) => setDriverId(value)}
              loading={driverLookup.isLoading}
              disabled={!customerId || customerLookup.isLoading}
              showSearch
              optionFilterProp="label"
              options={drivers.map((driver) => ({
                value: driver.id,
                label: `${driver.name ?? ""} ${driver.surname ?? ""}`.trim() || String(driver.id),
              }))}
              className="common-select w-[216px]"
              style={{ minWidth: 260 }}
            />
            <CustomSharedDatePicker
              label={t("date_picker_week_ending")}
              value={dateRange[1]}
              className="common-datepicker w-[216px]"
              style={{ minWidth: 260 }}
              allowClear={false}
              disabledDate={(current: Dayjs) => current?.isAfter(dayjs().endOf("day"))}
              onChange={handleWeekEndingChange}
            />
          </div>
          <div className="flex-none ml-3">
            <CustomButton label={t("reset")} onClick={handleReset} type="secondary" />
          </div>
        </div>

        {summaryStates.length > 0 && (
          <div className="mt-6 border border-gray3 px-6 py-4 rounded-2xl">
            <CustomTitle level={5} className="font-semibold !mb-4">
              {t("summary")}
            </CustomTitle>
            <div className="flex flex-wrap gap-6">
              {summaryStates.map((state) => (
                <div key={state} className="flex items-center gap-2 text-sm capitalize">
                  <span className="w-4 h-4 rounded" style={stateStyle(state)} />
                  <CustomText className="!text-gray10 !text-sm !font-bold">
                    {t(DRIVER_STATE_CONFIGS[state].nameKey)}
                  </CustomText>
                  <CustomText className="!text-gray8 !text-sm !font-bold">
                    {getTotalDurationByState(state, activityResponse)}
                  </CustomText>
                </div>
              ))}
            </div>
          </div>
        )}

        <Spin
          spinning={
            customerLookup.isLoading ||
            customerLookup.isFetching ||
            driverLookup.isLoading ||
            activityQuery.isLoading ||
            activityQuery.isFetching
          }
        >
          {!customerId || !driverId || statusCode === 204 || timelineDays.length === 0 ? (
            <Empty description={t("no_activity_available")} />
          ) : (
            <div style={{ display: "flex", gap: 18, overflowX: "auto" }}>
              <div style={{ flex: 1, minWidth: 700 }}>
                <div ref={chartContainerRef} style={{ width: "100%", height: chartHeight }} />
              </div>
              <div style={{ minWidth: 150, marginTop: 31 }}>
                {timelineDays.map((day) => (
                  <div key={day.date} style={{ height: barHeightPx, marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <CustomText className="!text-xs !font-medium !text-gray8">
                        {t("commitment")}:
                      </CustomText>
                      <CustomText className="!text-xs !font-semibold !text-gray10">
                        {day.commitment}
                      </CustomText>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <CustomText className="!text-xs !font-medium !text-gray8">
                        {t("driving")}:
                      </CustomText>
                      <CustomText className="!text-xs !font-semibold !text-gray10">
                        {day.drivingTime}
                      </CustomText>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Spin>

        <div className="mt-6 flex flex-wrap gap-6">
          {(Object.keys(DRIVER_STATE_CONFIGS) as DriverStateKey[]).map((state) => (
            <div key={state} className="flex items-center gap-2">
              <span className="h-4 w-4 rounded" style={stateStyle(state)} />
              <CustomText className="!text-sm !text-gray10">
                {t(DRIVER_STATE_CONFIGS[state].nameKey)}
              </CustomText>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span
              className="h-4 w-4 bg-white"
              style={{ borderBottom: "2px solid var(--color-red)", borderRadius: 0 }}
            />
            <CustomText className="!text-sm !text-gray10">DDD</CustomText>
          </div>
        </div>
      </CustomCard>
    </section>
  );
};

export default DriverActivitiesPage;
